import classNames from "classnames";
import { CartModal } from "components/CartModal/CartModal";
import { Button } from "components/DesignSystem/Button/Button";
import { MobileNav } from "components/MobileNav/MobileNav";
import { Notification } from "components/Notification";
import { CartMedium } from "components/icons/CartMedium";
import { Hamburger } from "components/icons/Hamburger";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useIsInChat } from "hooks/useIsInChat";
import { useIsInTickets } from "hooks/useInTickets";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ReactNode, useState } from "react";
import { useGetCartQuery } from "store/apis/products";

export const TopBar = ({
  nav,
  logo,
  children,
  border,
  subHeader,
  stickyNav,
  useInternalLink,
  shouldCenterLogo = true,
}: {
  nav?: ReactNode;
  logo?: ReactNode;
  children?: ReactNode;
  border?: boolean;
  subHeader?: ReactNode;
  stickyNav?: ReactNode;
  useInternalLink?: boolean;
  shouldCenterLogo?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  const { uuid: groupId, entities } = useCurrentGroupContext();

  const entityId = entities[0]?.uuid;

  const { data: cart } = useGetCartQuery(
    { groupId: groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const { isCustomer, isInvestor } = useRoleBasedView();

  const { isInChat } = useIsInChat();
  const { isInTickets } = useIsInTickets();
  const cartModal = useModal();

  const openNav = () => setOpen(true);
  const closeNav = () => setOpen(false);

  const showQuickActions = isCustomer && !isInvestor;
  const hasCartItems = Boolean(cart && cart.order_summary.items_count > 0);

  return (
    <div
      className={classNames(
        "t-relative t-flex t-px-6 t-gap-3 t-bg-surface-lighter-grey t-items-center sm:t-px-4 sm:t-gap-2",
        {
          "t-py-3": !isInChat && !isInTickets,
          "t-py-[11px]": isInChat || isInTickets,
          "t-border-solid t-border-b t-border-0 t-border-neutral-0 t-h-[60px]":
            border,
        }
      )}
    >
      <div className="t-shrink-0">
        <Button customType="ghost_icon" onClick={openNav} size="xsmall">
          <Hamburger width={20} height={20} />
        </Button>
      </div>

      <div
        className={classNames("t-shrink-0", {
          "sm:t-absolute sm:t-left-1/2 sm:-t-translate-x-1/2":
            shouldCenterLogo && !children,
        })}
      >
        {logo}
      </div>

      {showQuickActions && (
        <div className="t-ml-auto t-flex t-shrink-0 t-items-center t-gap-1 sm:t-hidden">
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={cartModal.open}
          >
            <div className="t-flex t-text-text-60">
              {hasCartItems && (
                <span className="t-border-solid t-border t-border-surface t-absolute t-right-0 t-top-0 t-h-2 t-w-2 t-rounded-full t-z-10 t-overflow-hidden">
                  <span className="t-bg-red-50 t-h-2 t-w-2 t-block" />
                </span>
              )}
              <CartMedium />
            </div>
          </Button>
          <Notification />
          {cartModal.isOpen && (
            <CartModal open={cartModal.isOpen} onClose={cartModal.close} />
          )}
        </div>
      )}

      <MobileNav
        open={open}
        onClose={closeNav}
        logo={logo}
        subHeader={subHeader}
        stickyNav={stickyNav}
        useInternalLink={useInternalLink}
      >
        {nav}
      </MobileNav>

      {children && <div className="t-min-w-0 t-shrink">{children}</div>}
    </div>
  );
};
